import React from 'react';
import { SE_BRAND, REPORT_REVIEW_MODES } from '../constants';
import {
  buildReportSectionKey,
  findPretextForSection,
  isPretextReportSection,
  reportHasPretextSections,
} from '../utils/reportPretext';
import { REPORT_SECTION_OPTIONS, getReportSectionAvailability } from '../utils/reportSections';
import {
  findBaselineSectionForClient,
  parseLabeledBilingualBlocks,
  resolveBilingualMetadataColumns,
  resolveBilingualSectionColumns,
} from '../utils/reportTranslation';
import { tUi } from '../utils/uiText';
import { getExportDownloadLanguageLabel } from '../utils/exportLanguage';

function ReviewColumn({ label, languageLabel, children, accent = 'gray' }) {
  const accentClass = accent === 'cyan' ? 'text-cyan-400 border-cyan-500/30' : 'text-teal-400 border-teal-500/30';
  const badgeClass = accent === 'cyan'
    ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40'
    : 'bg-teal-500/15 text-teal-300 border-teal-500/40';
  return (
    <div className={`rounded-lg border bg-gray-800/70 p-4 min-w-0 ${accentClass.split(' ').slice(1).join(' ')}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2 border-b border-gray-700/80 pb-2">
        <span className={`text-xs font-semibold uppercase tracking-wide ${accentClass.split(' ')[0]}`}>
          {label}
        </span>
        {languageLabel && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${badgeClass}`}>
            {languageLabel}
          </span>
        )}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function SideBySideBlock({ left, right, leftLabel, rightLabel, leftLanguage, rightLanguage, uiLanguage }) {
  if (!left && !right) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ReviewColumn label={leftLabel} languageLabel={leftLanguage}>
        {left || <span className="text-gray-500 text-sm italic">{tUi(uiLanguage, 'reportReviewEmptyDash')}</span>}
      </ReviewColumn>
      <ReviewColumn label={rightLabel} languageLabel={rightLanguage} accent="cyan">
        {right || <span className="text-gray-500 text-sm italic">{tUi(uiLanguage, 'reportReviewEmptyDash')}</span>}
      </ReviewColumn>
    </div>
  );
}

function LanguageBadge({ label, accent = 'teal' }) {
  const cls = accent === 'cyan'
    ? 'border-cyan-500/40 text-cyan-400 bg-cyan-950/30'
    : 'border-teal-500/40 text-teal-400 bg-teal-950/30';
  return (
    <span className={`inline-block mb-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>
      {label}
    </span>
  );
}

function TextBlock({ text }) {
  if (!text) return null;
  const blocks = parseLabeledBilingualBlocks(text);
  if (!blocks) {
    return <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">{text}</p>;
  }
  return (
    <div className="space-y-3">
      {blocks.map(({ label, content }, i) => (
        <div key={i} className={i > 0 ? 'pt-3 border-t border-gray-700/50' : ''}>
          <LanguageBadge label={label} accent={i === 0 ? 'teal' : 'cyan'} />
          {content && (
            <p className="text-gray-200 leading-relaxed whitespace-pre-wrap mt-1">{content}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function BulletList({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-2">
      {items.map((bullet, index) => {
        const blocks = parseLabeledBilingualBlocks(bullet);
        if (blocks) {
          return (
            <li key={index} className="space-y-2">
              {blocks.map(({ label, content }, bi) => (
                <div key={bi} className="flex items-start space-x-2 text-gray-200">
                  <span className="text-teal-400 mt-1 shrink-0">→</span>
                  <span>
                    <LanguageBadge label={label} accent={bi === 0 ? 'teal' : 'cyan'} />
                    {content && <span className="block whitespace-pre-wrap">{content}</span>}
                  </span>
                </div>
              ))}
            </li>
          );
        }
        return (
          <li key={index} className="flex items-start space-x-2 text-gray-200">
            <span className="text-teal-400 mt-1">→</span>
            <span>{bullet}</span>
          </li>
        );
      })}
    </ul>
  );
}

function SectionRemoveButton({ sectionIndex, section, pretexts, onRemoveSection, onExcludePretext, uiLanguage }) {
  const [confirming, setConfirming] = React.useState(false);
  const linkedPretext = findPretextForSection(section, pretexts);
  const isPretext = isPretextReportSection(section);

  if (!onRemoveSection && !onExcludePretext) return null;
  if (!isPretext && !onRemoveSection) return null;

  const handleConfirm = async () => {
    if (linkedPretext && onExcludePretext) {
      await onExcludePretext(linkedPretext.id);
    } else if (onRemoveSection) {
      await onRemoveSection(sectionIndex);
    }
    setConfirming(false);
  };

  if (confirming) {
    return (
      <div className="flex flex-col items-start gap-2 mb-3 max-w-xl">
        <span className="text-xs text-gray-400">
          {tUi(uiLanguage, 'reportReviewRemoveConfirmPrompt')}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleConfirm}
            className="px-3 py-1 text-xs bg-red-700 hover:bg-red-600 text-white font-semibold rounded-lg"
          >
            {tUi(uiLanguage, 'reportReviewRemoveConfirmYes')}
          </button>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
          >
            {tUi(uiLanguage, 'commonCancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setConfirming(true)}
      className="mb-3 px-3 py-1.5 text-xs bg-amber-950/50 hover:bg-amber-900/60 border border-amber-500/50 text-amber-200 rounded-lg"
    >
      {tUi(uiLanguage, 'reportReviewRemoveFromReportAndLibrary')}
    </button>
  );
}

function ReportBody({
  uiLanguage,
  reportData,
  sideBySide,
  baseline,
  sourceLabel,
  targetLabel,
  pretexts,
  onRemoveSection,
  onExcludePretext,
  allowSectionRemove,
}) {
  if (!reportData) return null;

  const renderMetadataField = (baselineValue, clientValue, key, { titleStyle = false } = {}) => {
    if (sideBySide && baseline) {
      const { english, arabic } = resolveBilingualMetadataColumns(baselineValue, clientValue);
      const leftNode = english
        ? (titleStyle
          ? <h2 className="text-2xl font-bold text-teal-400">{english}</h2>
          : <TextBlock text={english} />)
        : null;
      const rightNode = arabic
        ? (titleStyle
          ? <h2 className="text-2xl font-bold text-cyan-300">{arabic}</h2>
          : <TextBlock text={arabic} />)
        : null;
      return (
        <SideBySideBlock
          key={key}
          left={leftNode}
          right={rightNode}
          leftLabel={tUi(uiLanguage, 'reportReviewOriginal')}
          rightLabel={tUi(uiLanguage, 'reportReviewTranslation')}
          leftLanguage={sourceLabel}
          rightLanguage={targetLabel}
          uiLanguage={uiLanguage}
        />
      );
    }
    const fallback = clientValue || baselineValue;
    if (!fallback) return null;
    return titleStyle
      ? <h2 className="text-2xl font-bold text-teal-400 mb-2">{fallback}</h2>
      : <TextBlock text={fallback} />;
  };

  const title = sideBySide && baseline
    ? renderMetadataField(baseline.title, reportData.title, 'title', { titleStyle: true })
    : <h2 className="text-2xl font-bold text-teal-400 mb-2">{reportData.title}</h2>;

  const sections = reportData.sections || [];
  const baselineSections = baseline?.sections || [];

  return (
    <>
      <div className="mb-6">{title}</div>

      {(reportData.executive_summary || baseline?.executive_summary) && (
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-3">{tUi(uiLanguage, 'reportReviewExecutiveSummary')}</h3>
          {renderMetadataField(baseline?.executive_summary, reportData.executive_summary, 'exec')}
        </div>
      )}

      {reportData.exercise_execution_risk && typeof reportData.exercise_execution_risk === 'object' && (
        <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-teal-500/20">
          <h3 className="text-lg font-semibold text-white mb-2">{tUi(uiLanguage, 'reportReviewExerciseExecutionRisk')}</h3>
          <p className="text-xs text-gray-400 mb-3">
            {tUi(uiLanguage, 'reportReviewExerciseExecutionRiskHint')}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {Object.entries(reportData.exercise_execution_risk).map(([dim, level]) => (
              level ? (
                <div key={dim} className="flex justify-between text-sm border border-gray-700 rounded px-3 py-2">
                  <span className="text-gray-300 capitalize">{dim}</span>
                  <span className="font-medium text-teal-300">{level}</span>
                </div>
              ) : null
            ))}
          </div>
          {reportData.exercise_execution_risk_summary && (
            <div className="mt-3">
              <TextBlock text={reportData.exercise_execution_risk_summary} />
            </div>
          )}
        </div>
      )}

      {reportData.risk_score != null && !reportData.exercise_execution_risk && (
        <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-teal-500/20">
          <div className="flex items-center justify-between mb-2">
            <span className="text-lg font-semibold text-white">{tUi(uiLanguage, 'reportReviewOverallRiskScoreLegacy')}</span>
            <span className={`text-3xl font-bold ${
              reportData.risk_score >= 8 ? 'text-red-400' :
              reportData.risk_score >= 5 ? 'text-yellow-400' :
              'text-teal-400'
            }`}>
              {reportData.risk_score}/10
            </span>
          </div>
        </div>
      )}

      {sections.length > 0 && (
        <div className="space-y-4 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">
            {tUi(uiLanguage, 'reportReviewReportSections', { count: sections.length })}
          </h3>
          {sections.map((section, index) => {
            const baseSection = findBaselineSectionForClient(section, index, baselineSections);
            if (sideBySide && baseline) {
              const { heading, body, bullets } = resolveBilingualSectionColumns(baseSection, section);
              const { english: citationEn, arabic: citationAr } = resolveBilingualMetadataColumns(
                baseSection.citation,
                section.citation,
              );
              const headingLeft = heading.english
                ? <p className="text-lg font-semibold text-teal-300">{heading.english}</p>
                : null;
              const headingRight = heading.arabic
                ? <p className="text-lg font-semibold text-cyan-300">{heading.arabic}</p>
                : null;
              const bodyLeft = body.english ? <TextBlock text={body.english} /> : null;
              const bodyRight = body.arabic ? <TextBlock text={body.arabic} /> : null;
              const bulletsLeft = bullets.some((item) => item.english)
                ? <BulletList items={bullets.map((item) => item.english).filter(Boolean)} />
                : null;
              const bulletsRight = bullets.some((item) => item.arabic)
                ? <BulletList items={bullets.map((item) => item.arabic).filter(Boolean)} />
                : null;
              const showHeadingSideBySide = Boolean(headingLeft || headingRight);
              const showBodySideBySide = Boolean(bodyLeft || bodyRight);
              const showBulletsSideBySide = Boolean(bulletsLeft || bulletsRight);

              return (
                <div key={index} className="space-y-3">
                  {allowSectionRemove && (isPretextReportSection(section) || onRemoveSection) && (
                    <SectionRemoveButton
                      uiLanguage={uiLanguage}
                      sectionIndex={index}
                      section={section}
                      pretexts={pretexts}
                      onRemoveSection={onRemoveSection}
                      onExcludePretext={onExcludePretext}
                    />
                  )}
                  {showHeadingSideBySide && (
                    <SideBySideBlock
                      left={headingLeft}
                      right={headingRight}
                      leftLabel={`Section ${index + 1}`}
                      rightLabel={`Section ${index + 1}`}
                      leftLanguage={sourceLabel}
                      rightLanguage={targetLabel}
                      uiLanguage={uiLanguage}
                    />
                  )}
                  {showBodySideBySide && (
                    <SideBySideBlock
                      left={bodyLeft}
                      right={bodyRight}
                      leftLabel={tUi(uiLanguage, 'reportReviewBody')}
                      rightLabel={tUi(uiLanguage, 'reportReviewBody')}
                      leftLanguage={sourceLabel}
                      rightLanguage={targetLabel}
                      uiLanguage={uiLanguage}
                    />
                  )}
                  {showBulletsSideBySide && (
                    <SideBySideBlock
                      left={bulletsLeft}
                      right={bulletsRight}
                      leftLabel={tUi(uiLanguage, 'reportReviewBullets')}
                      rightLabel={tUi(uiLanguage, 'reportReviewBullets')}
                      leftLanguage={sourceLabel}
                      rightLanguage={targetLabel}
                      uiLanguage={uiLanguage}
                    />
                  )}
                  {(citationEn || citationAr) && (
                    <SideBySideBlock
                      left={citationEn ? <TextBlock text={citationEn} /> : null}
                      right={citationAr ? <TextBlock text={citationAr} /> : null}
                      leftLabel={tUi(uiLanguage, 'reportReviewCitation')}
                      rightLabel={tUi(uiLanguage, 'reportReviewCitation')}
                      leftLanguage={sourceLabel}
                      rightLanguage={targetLabel}
                      uiLanguage={uiLanguage}
                    />
                  )}
                </div>
              );
            }

            return (
              <div key={index} className="bg-gray-800 rounded-lg p-5 border border-teal-500/20">
                {allowSectionRemove && (isPretextReportSection(section) || onRemoveSection) && (
                  <SectionRemoveButton
                    uiLanguage={uiLanguage}
                    sectionIndex={index}
                    section={section}
                    pretexts={pretexts}
                    onRemoveSection={onRemoveSection}
                    onExcludePretext={onExcludePretext}
                  />
                )}
                <h4 className="text-lg font-semibold text-teal-400 mb-3">{section.heading}</h4>
                {section.body && <TextBlock text={section.body} />}
                {section.bullets?.length > 0 && (
                  <div className="mt-4 mb-4">
                    <BulletList items={section.bullets} />
                  </div>
                )}
                {section.citation && (
                  <div className="p-3 bg-gray-700 rounded border-l-4 border-teal-500 mt-4">
                    <p className="text-sm text-gray-300 mb-1">{tUi(uiLanguage, 'reportReviewSourceCitation')}</p>
                    <p className="text-sm text-teal-300 font-medium">{section.citation}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(reportData.recommendations?.length || baseline?.recommendations?.length) ? (
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">{tUi(uiLanguage, 'reportReviewPriorityRecommendations')}</h3>
          {sideBySide && baseline ? (
            <SideBySideBlock
              left={baseline.recommendations?.length ? (
                <ol className="space-y-2 list-none">
                  {baseline.recommendations.map((item, i) => (
                    <li key={i} className="flex gap-2 text-gray-200">
                      <span className="text-teal-400">{i + 1}.</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              ) : null}
              right={reportData.recommendations?.length ? (
                <ol className="space-y-2 list-none">
                  {reportData.recommendations.map((item, i) => (
                    <li key={i} className="flex gap-2 text-gray-200">
                      <span className="text-cyan-400">{i + 1}.</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ol>
              ) : null}
              leftLabel={tUi(uiLanguage, 'reportReviewOriginal')}
              rightLabel={tUi(uiLanguage, 'reportReviewTranslation')}
              leftLanguage={sourceLabel}
              rightLanguage={targetLabel}
              uiLanguage={uiLanguage}
            />
          ) : (
            <ul className="space-y-2">
              {(reportData.recommendations || []).map((item, index) => (
                <li key={index} className="flex items-start space-x-2 text-gray-200">
                  <span className="text-teal-400 mt-1">{index + 1}.</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {(reportData.vulnerabilities?.length || baseline?.vulnerabilities?.length) ? (
        <div className="space-y-4 mb-6">
          <h3 className="text-xl font-semibold text-white mb-4">
            {tUi(uiLanguage, 'reportReviewIdentifiedVulnerabilities', {
              count: (reportData.vulnerabilities || []).length,
            })}
          </h3>
          {(reportData.vulnerabilities || []).map((vuln, index) => {
            const baseVuln = baseline?.vulnerabilities?.[index] || {};
            if (sideBySide && baseline) {
              return (
                <div key={index} className="space-y-3">
                  <SideBySideBlock
                    left={baseVuln.technique ? <p className="font-semibold text-teal-300">{baseVuln.technique}</p> : null}
                    right={vuln.technique ? <p className="font-semibold text-cyan-300">{vuln.technique}</p> : null}
                    leftLabel={`Vulnerability ${index + 1}`}
                    rightLabel={`Vulnerability ${index + 1}`}
                    leftLanguage={sourceLabel}
                    rightLanguage={targetLabel}
                    uiLanguage={uiLanguage}
                  />
                  <SideBySideBlock
                    left={baseVuln.description ? <TextBlock text={baseVuln.description} /> : null}
                    right={vuln.description ? <TextBlock text={vuln.description} /> : null}
                    leftLabel={tUi(uiLanguage, 'reportReviewDescription')}
                    rightLabel={tUi(uiLanguage, 'reportReviewDescription')}
                    leftLanguage={sourceLabel}
                    rightLanguage={targetLabel}
                    uiLanguage={uiLanguage}
                  />
                </div>
              );
            }
            return (
              <div key={index} className="bg-gray-800 rounded-lg p-5 border border-teal-500/20">
                <h4 className="text-lg font-semibold text-teal-400 mb-3">{vuln.technique}</h4>
                <TextBlock text={vuln.description} />
              </div>
            );
          })}
        </div>
      ) : null}

      {(reportData.disclaimer || baseline?.disclaimer) && (
        <div className="mt-6 border-t border-teal-500/10 pt-4">
          {renderMetadataField(baseline?.disclaimer, reportData.disclaimer, 'disclaimer')}
        </div>
      )}
    </>
  );
}

function sectionHasRenderableContent(section) {
  if (!section) return false;
  if ((section.heading || '').trim()) return true;
  if ((section.body || '').trim()) return true;
  if ((section.citation || '').trim()) return true;
  if ((section.bullets || []).some((bullet) => String(bullet || '').trim().length > 0)) return true;
  return false;
}

function ReportSectionExclusions({ excludedSections, onChange, uiLanguage, reportData, baselineData }) {
  if (!onChange) return null;

  const excluded = new Set(excludedSections || []);

  const toggle = (sectionId) => {
    const next = new Set(excluded);
    if (next.has(sectionId)) {
      next.delete(sectionId);
    } else {
      next.add(sectionId);
    }
    onChange(Array.from(next));
  };

  return (
    <div className="mb-6 rounded-lg border border-teal-500/25 bg-gray-800/50 p-4">
      <p className="text-sm font-semibold text-teal-300 mb-2">{tUi(uiLanguage, 'reportReviewIncludeInReport')}</p>
      <p className="text-xs text-gray-400 mb-3">
        {tUi(uiLanguage, 'reportReviewIncludeInReportHint')}
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {REPORT_SECTION_OPTIONS.map((option) => {
          const hasContent = getReportSectionAvailability(reportData, option.id)
            || getReportSectionAvailability(baselineData, option.id);
          return (
            <label
              key={option.id}
              className={`flex items-center gap-2 text-sm ${hasContent ? 'text-gray-200 cursor-pointer' : 'text-gray-500 cursor-not-allowed'}`}
            >
              <input
                type="checkbox"
                className="rounded border-teal-500/40 bg-gray-900 text-teal-500 focus:ring-teal-500"
                checked={!excluded.has(option.id)}
                disabled={!hasContent}
                onChange={() => toggle(option.id)}
              />
              <span>{option.label}</span>
              {option.description && (
                <span className="text-[11px] text-gray-500">- {option.description}</span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ReportSectionItemExclusions({
  reportData,
  excludedSectionKeys,
  onChange,
  uiLanguage,
}) {
  if (!onChange) return null;
  const sections = reportData?.sections || [];
  const excluded = new Set(excludedSectionKeys || []);

  const toggle = (sectionKey) => {
    const next = new Set(excluded);
    if (next.has(sectionKey)) {
      next.delete(sectionKey);
    } else {
      next.add(sectionKey);
    }
    onChange(Array.from(next));
  };

  return (
    <div className="mb-6 rounded-lg border border-teal-500/25 bg-gray-800/50 p-4">
      <p className="text-sm font-semibold text-teal-300 mb-2">{tUi(uiLanguage, 'reportReviewIncludeInReport')}</p>
      <p className="text-xs text-gray-400 mb-3">
        Toggle individual sections for export. Empty sections stay disabled.
      </p>
      <div className="space-y-2">
        {sections.map((section, index) => {
          const sectionKey = buildReportSectionKey(section || {}, index);
          const hasContent = sectionHasRenderableContent(section);
          const label = (section?.heading || '').trim() || `Section ${index + 1}`;
          return (
            <label
              key={`section-toggle-${sectionKey}-${index}`}
              className={`flex items-center gap-2 text-sm ${hasContent ? 'text-gray-200 cursor-pointer' : 'text-gray-500 cursor-not-allowed'}`}
            >
              <input
                type="checkbox"
                className="rounded border-teal-500/40 bg-gray-900 text-teal-500 focus:ring-teal-500"
                checked={!excluded.has(sectionKey)}
                disabled={!hasContent}
                onChange={() => toggle(sectionKey)}
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function ReportReviewPanel({
  uiLanguage = 'en',
  report,
  baseline,
  reviewMode,
  onReviewModeChange,
  sourceLabel,
  targetLabel,
  reportQa,
  translatingReport,
  runningReportQa,
  exportLanguageMode,
  translateButtonLabel,
  onTranslate,
  onReportQa,
  onDownloadDocx,
  onDownloadPdf,
  onDownloadPptx,
  downloadingReportFormat,
  pretexts,
  onRemoveSectionFromReport,
  onExcludePretextFromReport,
  reportExcludedSections,
  onReportExcludedSectionsChange,
  reportExcludedSectionKeys,
  onReportExcludedSectionKeysChange,
}) {
  const hasComparison = Boolean(baseline);
  const downloadLanguageLabel = getExportDownloadLanguageLabel(
    exportLanguageMode,
    sourceLabel,
    targetLabel,
  );
  const sideBySide = reviewMode === 'side_by_side' && hasComparison;
  const displayReport = reviewMode === 'original' && baseline ? baseline : report;
  const compareBaseline = sideBySide ? baseline : null;
  const activeReport = displayReport;
  const allowSectionRemove = reportHasPretextSections(report)
    && (onRemoveSectionFromReport || onExcludePretextFromReport);

  return (
    <div className="mb-4 bg-gray-900 rounded-lg shadow-xl p-6 border border-teal-500/30">
      <div className="mb-6 pb-6 border-b border-teal-500/20 text-center">
        <div className="inline-block rounded-lg bg-black px-4 py-2 mb-3">
          <img
            src={SE_BRAND.logo}
            alt={SE_BRAND.companyName}
            className="mx-auto h-10 md:h-12 object-contain"
          />
        </div>
        <p className="text-sm font-semibold text-teal-400">{SE_BRAND.companyName}</p>
        {activeReport?.subtitle && !sideBySide && (
          <p className="text-sm text-gray-400 italic mt-1">{activeReport.subtitle}</p>
        )}
        {(SE_BRAND.website || SE_BRAND.phone) && (
          <p className="text-xs text-gray-500 mt-2">
            {SE_BRAND.website && (
              <a href={SE_BRAND.websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:text-teal-400">
                {SE_BRAND.website}
              </a>
            )}
            {SE_BRAND.website && SE_BRAND.phone && ' | '}
            {SE_BRAND.phone}
          </p>
        )}
      </div>

      {hasComparison && (
        <div className="mb-6 rounded-lg border border-cyan-500/25 bg-cyan-950/20 p-4">
          <p className="text-sm text-cyan-200 mb-3">
            {tUi(uiLanguage, 'reportReviewCompareHint')}
          </p>
          <div className="flex flex-wrap gap-2">
            {REPORT_REVIEW_MODES.map((mode) => (
              <button
                key={mode.code}
                type="button"
                onClick={() => onReviewModeChange(mode.code)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  reviewMode === mode.code
                    ? 'bg-cyan-500 text-black'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-teal-500/20'
                }`}
              >
                {mode.label}
              </button>
            ))}
          </div>
          {sideBySide && (
            <p className="text-xs text-gray-400 mt-3">
              {tUi(uiLanguage, 'reportReviewLeftLabel')}: {sourceLabel || tUi(uiLanguage, 'reportReviewOriginal')} · {tUi(uiLanguage, 'reportReviewRightLabel')}: {targetLabel || tUi(uiLanguage, 'reportReviewTranslation')}
            </p>
          )}
        </div>
      )}

      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex flex-col gap-1 text-sm text-gray-400">
          {(activeReport?.prepared_for || activeReport?.target_audience) && (
            <span>
              {tUi(uiLanguage, 'reportReviewPreparedFor')}:{' '}
              <span className="text-gray-300 font-medium">
                {activeReport.prepared_for || activeReport.target_audience}
              </span>
            </span>
          )}
          {(activeReport?.prepared_by || SE_BRAND.companyName) && (
            <span>
              {tUi(uiLanguage, 'reportReviewPreparedBy')}:{' '}
              <span className="text-gray-300 font-medium">
                {activeReport.prepared_by || SE_BRAND.companyName}
              </span>
            </span>
          )}
          {activeReport?.generated_at && (
            <span>{tUi(uiLanguage, 'reportReviewGenerated')}: {new Date(activeReport.generated_at).toLocaleString()}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onTranslate}
            disabled={translatingReport}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-700 border border-teal-500/30 text-white font-semibold rounded-lg text-sm transition-all duration-200"
          >
            {translatingReport ? tUi(uiLanguage, 'reportReviewTranslating') : translateButtonLabel}
          </button>
          <button
            type="button"
            onClick={onReportQa}
            disabled={runningReportQa}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-700 border border-cyan-500/30 text-cyan-300 font-semibold rounded-lg text-sm transition-all duration-200"
          >
            {runningReportQa ? tUi(uiLanguage, 'reportReviewRunningQa') : tUi(uiLanguage, 'reportReviewTranslationQa')}
          </button>
          <button
            type="button"
            onClick={onDownloadDocx}
            disabled={downloadingReportFormat === 'docx'}
            className="px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 disabled:text-gray-400 text-black font-semibold rounded-lg text-sm transition-all duration-200"
          >
            {downloadingReportFormat === 'docx'
              ? tUi(uiLanguage, 'reportReviewPreparingDocx')
              : tUi(uiLanguage, 'reportReviewDownloadDocx', {
                language: downloadLanguageLabel,
              })}
          </button>
          <button
            type="button"
            onClick={onDownloadPdf}
            disabled={downloadingReportFormat === 'pdf'}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-700 border border-teal-500/30 text-white font-semibold rounded-lg text-sm transition-all duration-200"
          >
            {downloadingReportFormat === 'pdf'
              ? tUi(uiLanguage, 'reportReviewPreparingPdf')
              : tUi(uiLanguage, 'reportReviewDownloadPdf', {
                language: downloadLanguageLabel,
              })}
          </button>
          <button
            type="button"
            onClick={onDownloadPptx}
            disabled={downloadingReportFormat === 'pptx'}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-700 border border-amber-500/30 text-amber-200 font-semibold rounded-lg text-sm transition-all duration-200"
          >
            {downloadingReportFormat === 'pptx'
              ? tUi(uiLanguage, 'reportReviewPreparingDeck')
              : tUi(uiLanguage, 'reportReviewExportDeck')}
          </button>
        </div>
      </div>

      {reportQa && (
        <div className={`mb-6 rounded-lg border p-4 text-sm ${
          reportQa.status === 'pass'
            ? 'border-cyan-500/30 bg-cyan-950/30 text-cyan-200'
            : 'border-amber-500/30 bg-amber-950/20 text-amber-200'
        }`}>
          <p className="font-semibold mb-2">
            {tUi(uiLanguage, 'reportReviewTranslationQa')} - {reportQa.target_language_label}: {reportQa.status === 'pass'
              ? tUi(uiLanguage, 'reportReviewPass')
              : tUi(uiLanguage, 'reportReviewRecommended')}
          </p>
          {(reportQa.issues || []).length === 0 ? (
            <p>{tUi(uiLanguage, 'reportReviewNoIssuesFlagged')}</p>
          ) : (
            <ul className="list-disc pl-5 space-y-1">
              {reportQa.issues.map((issue, index) => (
                <li key={`qa-issue-${index}`}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ReportSectionExclusions
        excludedSections={reportExcludedSections}
        onChange={onReportExcludedSectionsChange}
        uiLanguage={uiLanguage}
        reportData={displayReport}
        baselineData={baseline}
      />
      <ReportSectionItemExclusions
        excludedSectionKeys={reportExcludedSectionKeys}
        onChange={onReportExcludedSectionKeysChange}
        uiLanguage={uiLanguage}
        reportData={displayReport}
      />

      {allowSectionRemove && (
        <p className="mb-4 text-sm text-teal-200/90 bg-teal-950/30 border border-teal-500/30 rounded-lg px-3 py-2">
          <strong className="text-teal-300">{tUi(uiLanguage, 'reportReviewRemoveFromReportAndLibrary')}</strong> {tUi(uiLanguage, 'reportReviewRemoveFromReportAndLibraryHint1')}{' '}
          <strong className="text-teal-300">{tUi(uiLanguage, 'reportReviewRemoveFromPretextLibraryOnly')}</strong> {tUi(uiLanguage, 'reportReviewRemoveFromReportAndLibraryHint2')}
        </p>
      )}

      <ReportBody
        reportData={reviewMode === 'original' && baseline ? baseline : report}
        baseline={compareBaseline}
        sideBySide={sideBySide}
        sourceLabel={sourceLabel}
        targetLabel={targetLabel}
        pretexts={pretexts}
        onRemoveSection={onRemoveSectionFromReport}
        onExcludePretext={onExcludePretextFromReport}
        allowSectionRemove={allowSectionRemove}
        uiLanguage={uiLanguage}
      />
    </div>
  );
}
